import { useContext, useState } from "react";
import HotelContext from "./HotelContext";
import { HotelProviderProps, SearchHotels } from "./type";
import { Formatter } from "utils/formatters";
import { Hotel, Hotels } from "containers/PageHome/type";
import { StaySearchFormFields } from "components/HeroSearchForm/type";
import { Validation } from "utils/validations";
import { fetchHotels } from "services/fetch-hotels";
import { AxiosError } from "axios";

export function HotelProvider({ children }: Readonly<HotelProviderProps>) {
  const [destination, setDestination] = useState<number | undefined>();
  const [checkInDate, setCheckInDate] = useState<string>(
    Formatter.prettyDateHifen(Validation.getTomorrowsDate())
  );
  const [numNights, setNumNights] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [hotels, setHotels] = useState<Hotels>([]);
  const [hotel, setHotel] = useState<Hotel>({} as Hotel);
  const [messageError, setMessageError] = useState<string>("");
  const [submitSearchHotels, setSubmitSearchHotels] = useState<boolean>(false);
  const [destinationName, setDestinationName] = useState<string>("");
  const [showHeroSearch, setShowHeroSearch] = useState<
    StaySearchFormFields | null | undefined
  >();
  const [searchEngine, setSearchEngine] = useState<boolean>(true);
  const [guests, setGuests] = useState<{
    adults: number;
    children: number;
  }>({
    adults: 1,
    children: 0,
  });
  const [isBooking, setIsBooking] = useState(false);
  const [token, setToken] = useState<string>("");
  const [descriptionRoom, setDescriptionRoom] = useState<string | undefined>(
    ""
  );
  const [hotelAddress, setHotelAddress] = useState<string>("");
  const [hotelName, setHotelName] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [pageReload, setPageReload] = useState(false);
  const [hotelSearchFirst, setHotelSearchFirst] = useState<number | null>(null);
  const [getHotelSearchFirst, setGetHotelSearchFirst] = useState<number | null>(null);
  const [hotelSearch, setHotelSearch] = useState<Hotel | undefined>();
  const [hiddenHero, setHiddenHero] = useState<boolean>(true);

  const searchHotels = async ({
    destination,
    numNights,
    checkInDate,
    guests,
  }: SearchHotels) => {
    setLoading(true);
    setMessageError("");
    try {
      const data: Hotels = await fetchHotels(
        destination,
        numNights,
        checkInDate,
        guests?.adults ?? 1,
        guests?.children ?? 0,
        guests?.agesChildren ?? []
      );
      if (data.length >= 1) {
        setHotels(data);
        setLoading(false);
        setToken(data[0].token);
      } else if (data.length === 0 && destination === undefined) {
        setMessageError(
          "Um problema inesperado ocorreu. Aguarde alguns minutos e tente novamente."
        );
        setLoading(false);
        setToken("");
      }
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        setMessageError(
          "não temos nenhum hotel disponível para essa seleção."
        );
        setLoading(false);
        setToken("");
      }
      setLoading(false);
    } finally {
      const currentTime = new Date().getTime();
      const currentDate = new Date().toDateString();
      const getLocalStorage = JSON.parse(localStorage.getItem('lastModalShownTime') || '{}');

      if (getLocalStorage) {
        const { date: lastDate } = getLocalStorage;

        if (currentDate !== lastDate) {
          localStorage.setItem('lastModalShownTime', JSON.stringify({ time: currentTime, date: currentDate }));
        }
      }
      setLoading(false);
    }
  };

  const contextValue = {
    destination,
    setDestination,
    checkInDate,
    setCheckInDate,
    numNights,
    setNumNights,
    loading,
    setLoading,
    hotels,
    setHotels,
    hotel,
    setHotel,
    messageError,
    setMessageError,
    submitSearchHotels,
    setSubmitSearchHotels,
    destinationName,
    setDestinationName,
    showHeroSearch,
    setShowHeroSearch,
    guests,
    setGuests,
    isBooking,
    setIsBooking,
    token,
    setToken,
    searchHotels,
    descriptionRoom,
    setDescriptionRoom,
    hotelAddress,
    setHotelAddress,
    hotelName,
    setHotelName,
    showModal,
    setShowModal,
    setPageReload,
    pageReload,
    hotelSearchFirst,
    setHotelSearchFirst,
    setGetHotelSearchFirst,
    getHotelSearchFirst,
    hotelSearch,
    setHotelSearch,
    searchEngine,
    setSearchEngine,
    hiddenHero,
    setHiddenHero,
  };

  return (
    <HotelContext.Provider value={contextValue}>
      {children}
    </HotelContext.Provider>
  );
}

export const useHotels = () => {
  const context = useContext(HotelContext);
  if (!context) {
    throw new Error("useHotelDestination must be used within a HotelProvider");
  }
  return context;
};
